import React, { useState, useEffect, forwardRef } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DatePicker.module.scss";
import CalendarImg from "../../../assets/images/packages/CalendarDots.svg";

interface CustomDatePickerProps {
  value: string;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  isTimePicker?: boolean;
  minTime?: Date;
  maxTime?: Date;
  readOnly?: boolean;
}

const CustomInput = forwardRef<HTMLInputElement, any>( // Use forwardRef here
  ({ value, onClick, onChange, placeholder }, ref) => (
    <input
      type="text"
      onClick={onClick}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      ref={ref}
      className={styles.customDatePickerInput}
      readOnly
      style={{ width: "100%" }}
    />
  ),
);

const DatePicker = forwardRef<HTMLInputElement, CustomDatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder,
      min,
      max,
      readOnly,
      minTime,
      maxTime,
      isTimePicker,
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    };

    const formatDate = (date: Date | null): string => {
      if (!date) return "";
      const pad = (n: number) => String(n).padStart(2, "0");
      const yyyy = date.getFullYear();
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const mi = pad(date.getMinutes());
      const ss = pad(date.getSeconds());
      return isTimePicker
        ? `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
        : `${yyyy}-${mm}-${dd}`;
    };

    const handleDateChange = (date: Date | null) => {
      const dateStr = formatDate(date);
      setInternalValue(dateStr);
      onChange(date);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInternalValue(val);

      const date = parseDate(val);
      if (date) {
        onChange(date);
      }
    };

    const minDate = min ? (parseDate(min) ?? undefined) : undefined;
    const maxDate = max ? (parseDate(max) ?? undefined) : undefined;
    const selectedDate = parseDate(internalValue);

    return (
      <div className={styles.dateINput}>
        <ReactDatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat={isTimePicker ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"}
          minDate={minDate}
          maxDate={maxDate}
          placeholderText={placeholder}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          {...(isTimePicker && {
            minTime,
            maxTime,
            showTimeSelect: true,
            timeFormat: "HH:mm",
            timeIntervals: 30,
            timeCaption: "Time",
          })}
          customInput={
            <CustomInput
              ref={ref}
              placeholder={placeholder}
              value={internalValue}
              onChange={handleInputChange}
              readOnly={readOnly}
            />
          }
          readOnly={readOnly}
        />
        <img
          src={CalendarImg}
          alt="calendar icon"
          className={styles.dateIcon}
          onClick={() => {
            const input = document.querySelector(
              `.${styles.customDatePickerInput}`,
            ) as HTMLInputElement;
            if (input) {
              input.click(); // Təqvimi açmaq üçün doğru üsul
            }
          }}
        />
      </div>
    );
  },
);

export default DatePicker;
